import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useBoolean } from '@fluentui/react-hooks';
import {
  IconButton,
  IIconProps,
  Label,
  MessageBarType,
  PrimaryButton,
  Separator,
  Spinner,
  Text
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';

// models
import { PersonIdentifierViewModel } from '../../../models/Entities/Person/Identifier/PersonIdentifierModels';

// components
import { ConfirmationButtonComponent } from '../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import PersonIdentifierFormComponent from './PersonIdentifier.Form.Component';

// functions
import * as CommonFunctions from '../Persons.Functions';
import * as ComponentFunctions from './PersonIdentifiers.List.Functions';

const EditIcon: IIconProps = { iconName: 'Edit' };
const AddIcon: IIconProps = { iconName: 'Add' };

const PersonIdentifiersListComponent = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();

  const [isFormOpen, { setTrue: openForm, setFalse: dismissForm }] = useBoolean(false);
  const [selectedIdentifierId, setSelectedIdentifierId] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (props.PersonId) props.GetIdentifiersByPersonId(props.PersonId);
  }, [props.PersonId]);

  const handleOpenCreate = () => {
    setSelectedIdentifierId(undefined);
    openForm();
  };

  const handleEdit = (identifierId: number) => {
    setSelectedIdentifierId(identifierId);
    openForm();
  };

  const handleDismissForm = () => {
    setSelectedIdentifierId(undefined);
    dismissForm();
  };

  return (
    <>
      <div className="ComponentHeaderControls">
        <PrimaryButton
          text={t('persons.identifiers.list.add', { defaultValue: 'Agregar identificación' })}
          iconProps={AddIcon}
          onClick={handleOpenCreate}
          className="actionButton"
        />
      </div>
      <Separator />

      {props.failOnLoading && (
        <MessageComponent
          message={t('persons.identifiers.list.error', {
            defaultValue: 'No fue posible obtener las identificaciones'
          })}
          subMessage={props.error?.ErrorMessage || ''}
          type={MessageBarType.error}
          itemList={props.error?.Errors || []}
        />
      )}

      {!props.isLoading && props.identifiers.length === 0 && (
        <MessageComponent
          message={t('persons.identifiers.list.empty', { defaultValue: 'No hay identificaciones registradas' })}
          type={MessageBarType.info}
        />
      )}

      {props.identifiers.length > 0 && (
        <table className="Table">
          <thead>
            <tr>
              <th>{t('persons.identifiers.list.identifierType', { defaultValue: 'Tipo' })}</th>
              <th>{t('persons.identifiers.list.identifier', { defaultValue: 'Identificador' })}</th>
              <th>{t('persons.identifiers.list.issuedBy', { defaultValue: 'Emitido por' })}</th>
              <th>{t('persons.identifiers.list.issueDate', { defaultValue: 'Emisión' })}</th>
              <th>{t('persons.identifiers.list.expirationDate', { defaultValue: 'Vencimiento' })}</th>
              <th>{t('persons.identifiers.list.verified', { defaultValue: 'Verificado' })}</th>
              <th style={{ width: '140px' }}>
                {t('persons.common.actions', { defaultValue: 'Acciones' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {props.identifiers.map((item: PersonIdentifierViewModel) => (
              <tr key={item.id} className="TableBodyRow">
                <td>
                  <Text>{item.identifierType?.name || '-'}</Text>
                </td>
                <td className="primary">
                  <Label>{item.identifier || '-'}</Label>
                </td>
                <td>
                  <Text>{item.issuedBy || '-'}</Text>
                </td>
                <td>
                  <Text>{CommonFunctions.FormatDate(item.issueDate)}</Text>
                </td>
                <td>
                  <Text>{CommonFunctions.FormatDate(item.expirationDate)}</Text>
                </td>
                <td>
                  <Text>
                    {item.hasBeenVerified
                      ? t('persons.common.yes', { defaultValue: 'Sí' })
                      : t('persons.common.no', { defaultValue: 'No' })}
                  </Text>
                </td>
                <td style={{ width: '140px' }}>
                  <IconButton
                    iconProps={EditIcon}
                    title={t('persons.common.edit', { defaultValue: 'Editar' })}
                    ariaLabel={t('persons.common.edit', { defaultValue: 'Editar' })}
                    onClick={() => handleEdit(item.id)}
                  />
                  <div className="danger">
                    <ConfirmationButtonComponent
                      ButtonId={`PersonIdentifierDeleteButton-${item.id}`}
                      ButtonIconName={'Delete'}
                      ButtonText={''}
                      ButtonCssClass={''}
                      ConfirmationButtonText={t('persons.common.delete', { defaultValue: 'Eliminar' })}
                      ConfirmationButtonCssClass={'deleteButton'}
                      CancelButtonText={t('persons.common.cancel', { defaultValue: 'Cancelar' })}
                      CancelButtonCssClass={''}
                      Title={t('persons.identifiers.list.deleteTitle', {
                        defaultValue: 'Eliminar identificación'
                      })}
                      Description={t('persons.identifiers.list.deleteDescription', {
                        defaultValue: '¿Desea eliminar la identificación seleccionada?'
                      })}
                      ElementTextHeader={t('persons.identifiers.list.identifier', {
                        defaultValue: 'Identificador'
                      })}
                      ElementTextInformation={item.identifier || ''}
                      onConfirmation={() => props.DeletePersonIdentifier(item.id)}
                      onCancel={() => undefined}
                      isOperationExecuting={props.isDeleting || false}
                      isOperationExecutedWithError={props.failOnDeleting || false}
                      OperationError={props.error}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {props.isLoading && (
        <div>
          <Spinner
            label={t('persons.identifiers.list.loading', { defaultValue: 'Cargando identificaciones...' })}
            ariaLive="assertive"
            labelPosition="top"
          />
        </div>
      )}

      <PersonIdentifierFormComponent
        isOpen={isFormOpen}
        openPanel={openForm}
        dismissPanel={handleDismissForm}
        PersonId={props.PersonId}
        PersonType={props.PersonType}
        identifierId={selectedIdentifierId}
      />
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(PersonIdentifiersListComponent as any);
